import Feature from 'ol/Feature';
import OpenLayers from 'client/utils/helpers/OpenLayers/OpenLayers';
import {
    isValidFeature,
    updateNotifications
} from 'client/pages/MapPage/layers/styles/drawStyle';
import { getValidModifyStyle } from 'client/pages/MapPage/layers/styles/validStyle';
import { getErrorModifyStyle } from 'client/pages/MapPage/layers/styles/errorStyle';
import { addVertex } from 'client/pages/MapPage/layers/styles/vertexStyle';
import { addExtent } from 'client/pages/MapPage/layers/styles/extentStyle';
import { LINE_STRING } from 'client/utils/constants/geometryType';
import store from 'client/store/store';
import { drawStates } from 'client/utils/constants/appStates';
import { isIncompleteLineString } from './utils';
import {
    getValidationPolicy,
    resolveValidation,
    resolveFallbackValidationFlow
} from 'client/pages/MapPage/layers/drawValidation';
import { isFeatureWithinServiceArea } from 'client/utils/map/serviceAreaUtils';

const { INVALID, EXCEEDS_EXTENT, OUTSIDE_SERVICE_AREA } = drawStates;

const styleFunction = (feature, userLimit, bufferRadiusMetres, drawType, lineWidthLimit) => {
    if (feature && feature instanceof Feature) {
        const styles = [];
        const geometry = feature?.getGeometry();
        if (geometry) {
            const geometryType = geometry.getType();
            const policy = getValidationPolicy(drawType);

            const evaluationContext = {
                drawType,
                geometryType,
                geometry,
                userLimit,
                feature,
                isSketchEdgeGeometry: false
            };

            const resolvedValidation = policy
                ? resolveValidation(evaluationContext, policy)
                : resolveFallbackValidationFlow(geometryType, geometry, {
                    isLineComplete: (lineGeometry) => !isIncompleteLineString(lineGeometry)
                });

            const complete = resolvedValidation.complete;
            const shouldValidateGeometry = resolvedValidation.shouldValidateGeometry;
            const shouldValidateExtent = resolvedValidation.shouldValidateExtent;
            const shouldNotify = resolvedValidation.shouldNotify;
            const defaultExtentMessageDescriptor = resolvedValidation.extentMessageDescriptor;
            const policyContext = resolvedValidation.policyContext || evaluationContext;

            const drawStatus = store.getState?.()?.draw?.status;
            const hasGlobalInvalid = Array.isArray(drawStatus) && drawStatus.includes(INVALID);
            const hasGlobalExceeds = Array.isArray(drawStatus) && drawStatus.includes(EXCEEDS_EXTENT);
            const hasGlobalOutsideServiceArea = Array.isArray(drawStatus) && drawStatus.includes(OUTSIDE_SERVICE_AREA);
            const safeBufferRadiusMetres = Number.isFinite(bufferRadiusMetres) ? bufferRadiusMetres : undefined;

            const isGreaterThan = shouldValidateExtent
                ? (policy ? policy.getExtentExceeded(policyContext) : OpenLayers.geometry.isGreaterThan(geometry, userLimit))
                : false;

            const isLineWidthExceeded =
                drawType &&
                geometryType === LINE_STRING &&
                Number.isFinite(lineWidthLimit) &&
                (safeBufferRadiusMetres * 2) > lineWidthLimit;

            const exceedsLimit = Boolean(isGreaterThan || isLineWidthExceeded);

            const extentMessageDescriptor = isLineWidthExceeded
                ? { id: 'mapPage.error.lineTooWide', values: { extent: lineWidthLimit } }
                : defaultExtentMessageDescriptor;

            const isValid = shouldValidateGeometry ? isValidFeature(feature) : true;

            const validModifyStyle = getValidModifyStyle(safeBufferRadiusMetres);
            const errorModifyStyle = getErrorModifyStyle(safeBufferRadiusMetres);

            const shouldForceErrorForLine = geometryType === LINE_STRING && complete && (hasGlobalInvalid || hasGlobalExceeds || hasGlobalOutsideServiceArea);

            // Service area containment check
            let isOutsideServiceArea = false;
            if (complete) {
                const croppedServiceArea = store.getState?.()?.coverageAssetOwners?.croppedServiceArea;
                if (croppedServiceArea && croppedServiceArea.type && croppedServiceArea.coordinates) {
                    isOutsideServiceArea = !isFeatureWithinServiceArea(feature, croppedServiceArea);
                }
            }

            const drawStyle = (!shouldForceErrorForLine && !exceedsLimit && isValid && !isOutsideServiceArea)
                ? validModifyStyle[geometryType]
                : errorModifyStyle[geometryType];
            if (!drawStyle) {
                return new Error('style: invalid geometry type');
            }

            if (shouldNotify) {
                updateNotifications(geometryType, exceedsLimit, isValid, { extentMessageDescriptor, isOutsideServiceArea });
            }

            if (Array.isArray(drawStyle)) {
                styles.push(...drawStyle);
            } else {
                styles.push(drawStyle);
            }

            addVertex(geometry, styles);
            if (geometryType !== LINE_STRING) {
                addExtent(geometry, styles);
            }
        }
        return styles;
    } else {
        return new Error('style: invalid feature type');
    }
};

export default styleFunction;
